<template>
    <TransitionRoot as="template" :show="modelValue">
        <Dialog as="div" class="relative z-40" @close="closeOutside" :initialFocus="initialFocusRef">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-600 dark:bg-opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-40 overflow-y-auto">
                <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                        <DialogPanel
                            class="relative transform rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 w-full"
                            :class="{
                                'sm:max-w-sm': mini,
                                'sm:max-w-lg': small || sizeNotSet,
                                'sm:max-w-xl': medium,
                                'sm:max-w-2xl': large,
                                'sm:max-w-4xl': huge,
                            }"
                        >
                            <div class="absolute top-0 right-0 hidden pt-4 pr-4 sm:block z-20">
                                <button
                                    v-if="closeIconOnly"
                                    type="button"
                                    class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                    @click="close"
                                >
                                    <i class="h-6 w-6 fa-sharp fa-times" aria-hidden="true"/>
                                </button>
                            </div>

                            <div class="bg-white"
                                 :class="[
                                     ! fullWidth && 'rounded-t-lg px-4 pt-5 pb-4 sm:p-6 sm:pb-4',
                                     fullWidth && 'rounded-lg',
                                     closeIconOnly && 'rounded-b-xl'
                                 ]"
                            >
                                <DialogTitle v-if="$slots.title || title" as="h3" class="text-lg leading-6 font-medium text-gray-900 mb-2">
                                    <slot name="title">
                                        {{ title }}
                                    </slot>
                                </DialogTitle>

                                <slot/>
                            </div>

                            <div
                                v-if="(!closeIconOnly && !manualClose) || $slots.buttons"
                                class="bg-gray-50 px-4 sm:px-6 py-3 flex rounded-b-lg sm:flex-row flex-col-reverse space-y-2 space-y-reverse sm:space-y-0"
                            >
                                <QrButton
                                    v-if="!manualClose"
                                    secondary
                                    type="button"
                                    @click="close"
                                    class="ml-0 mr-auto w-full sm:w-auto"
                                    :text="closeText ?? trans('button.cancel')"
                                    ref="closeButtonRef"
                                />

                                <slot name="buttons"/>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { computed, ref, onMounted } from 'vue';
import { trans } from "@/libs/helper";
import QrButton from "@/components/application-ui/elements/QrButton.vue";

const emit = defineEmits([
    'on-close', 'update:modelValue'
])

const props = defineProps({
    modelValue: Boolean,
    huge: Boolean,
    large: Boolean,
    medium: Boolean,
    small: Boolean,
    mini: Boolean,
    closeable: {
        required: false,
        type: Boolean,
        default: true,
    },
    closeText: String,
    title: String,
    fullWidth: Boolean,
    manualClose: Boolean,
    closeIconOnly: {
        required: false,
        type: Boolean,
        default: false
    },
    initialFocus: {
        required: false,
    }
})

const initialFocusRef = ref(null)
const closeButtonRef = ref(null)

const closeOutside = () => {
    if (props.closeable) {
        close()
    }
}

const close = () => {
    emit('on-close')
    emit('update:modelValue', false)
}

const sizeNotSet = computed(() => {
    return !props.huge && !props.large && !props.medium && !props.small && !props.mini
})

onMounted(() => {
    if (props.initialFocus) {
        initialFocusRef.value = props.initialFocus
    } else {
        initialFocusRef.value = closeButtonRef
    }
})
</script>
